import React, { useEffect, useMemo, useState } from 'react';
import { mediaUrl } from '../../../utils/mediaUrl';
import { Link } from 'react-router-dom';
import { AdminSidebar } from '../AdminDashboardPage';
import AdminHeader from '../AdminHeader';
import { getAllOrders, downloadOrdersPdf } from '../../../services/orderService';
import formatPrice from '../../../utils/formatPrice';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
} from 'chart.js';
import '../../../css/salesReport.css';
import {
  ShoppingBag,
  TrendingUp,
  Package,
  AlertTriangle,
  FileSpreadsheet,
  FileText,
  Calendar,
  CalendarX,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
} from 'lucide-react';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, LineElement, PointElement, Filler);

// Match the dashboard's chart typography + tooltip
ChartJS.defaults.font.family = "'Figtree', 'Segoe UI', system-ui, sans-serif";
ChartJS.defaults.font.size = 12;
ChartJS.defaults.color = '#8f8178';
Object.assign(ChartJS.defaults.plugins.tooltip, {
  backgroundColor: '#211915',
  titleColor: '#f5ede6',
  bodyColor: '#f5ede6',
  padding: 10,
  cornerRadius: 8,
  displayColors: false,
});

const PALETTE = ['#f4b6c4', '#4a6382', '#2e6a4f', '#c48a2c', '#8a6a9e', '#3d8796', '#d9466f', '#8f8178'];
const GRID_COLOR = '#f0ebe6';
const AXIS_COLOR = '#e7dfd8';
const DAY_MS = 24 * 60 * 60 * 1000;

const PAYMENT_LABELS = {
  COD: 'Cash on delivery',
  cod: 'Cash on delivery',
  Razorpay: 'Razorpay',
  razorpay: 'Razorpay',
  UPI: 'UPI',
  Card: 'Card',
};

const LOST_STATUSES = ['Cancelled', 'Rejected', 'Returned'];
const REFUND_STATUSES = ['processed', 'pending', 'manual'];

// Local-time date helpers (toISOString would shift days for IST evenings)
const toISODate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseDay = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const daysBetween = (a, b) => Math.round((parseDay(b) - parseDay(a)) / DAY_MS) + 1;
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const fmtDay = (d, withYear = false) =>
  d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}) });
const fmtMonth = (d) => d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

const formatCompact = (v) => {
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1).replace(/\.0$/, '')}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `₹${Math.round(v)}`;
};

const PRESETS = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'month', label: 'This month' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

const rangeForPreset = (preset) => {
  const today = new Date();
  const to = toISODate(today);
  if (preset === '7d') return { from: toISODate(addDays(today, -6)), to };
  if (preset === '30d') return { from: toISODate(addDays(today, -29)), to };
  if (preset === 'month') return { from: toISODate(new Date(today.getFullYear(), today.getMonth(), 1)), to };
  if (preset === 'all') return { from: '2020-01-01', to };
  return { from: to, to };
};

const ordersInRange = (orders, from, to) => {
  const fromTime = parseDay(from).getTime();
  const toTime = parseDay(to).getTime() + DAY_MS - 1;
  return orders.filter((o) => {
    const t = new Date(o.createdAt).getTime();
    return t >= fromTime && t <= toTime;
  });
};

const refundOf = (o) => (REFUND_STATUSES.includes(o.refund?.status) ? Number(o.refund?.amount) || 0 : 0);

const computeStats = (orders) => {
  const live = orders.filter((o) => !LOST_STATUSES.includes(o.status));
  const lost = orders.filter((o) => LOST_STATUSES.includes(o.status));

  const refunds = live.reduce((sum, o) => sum + refundOf(o), 0);
  const grossRevenue = live.reduce((sum, o) => sum + (o.totalPrice || 0), 0);
  const revenue = Math.max(0, grossRevenue - refunds);
  const paidRevenue = live
    .filter((o) => o.isPaid)
    .reduce((sum, o) => sum + (o.totalPrice || 0) - refundOf(o), 0);
  const outstandingRevenue = Math.max(0, revenue - paidRevenue);
  const lostRevenue = lost.reduce((sum, o) => sum + (o.totalPrice || 0), 0);

  const orderCount = live.length;
  const avgOrderValue = orderCount ? Math.round(revenue / orderCount) : 0;
  const itemsSold = live.reduce(
    (sum, o) => sum + (o.orderItems?.reduce((s, it) => s + (it.qty || 0), 0) || 0),
    0
  );
  const taxCollected = live.reduce((sum, o) => sum + (o.taxPrice || 0), 0);
  const shippingCollected = live.reduce((sum, o) => sum + (o.shippingPrice || 0), 0);

  const paymentMap = {};
  live.forEach((o) => {
    const key = o.paymentMethod || 'COD';
    if (!paymentMap[key]) paymentMap[key] = { revenue: 0, orders: 0 };
    paymentMap[key].revenue += o.totalPrice || 0;
    paymentMap[key].orders += 1;
  });
  const paymentBreakdown = Object.entries(paymentMap)
    .map(([method, v]) => ({ method, ...v }))
    .sort((a, b) => b.revenue - a.revenue);

  const productMap = {};
  live.forEach((o) => {
    (o.orderItems || []).forEach((it) => {
      if (!it.name) return;
      if (!productMap[it.name]) productMap[it.name] = { totalSold: 0, revenue: 0, image: it.image };
      productMap[it.name].totalSold += it.qty || 0;
      productMap[it.name].revenue += (it.price || 0) * (it.qty || 0);
    });
  });
  const topProducts = Object.entries(productMap)
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  return {
    live,
    revenue,
    grossRevenue,
    refunds,
    paidRevenue,
    outstandingRevenue,
    lostRevenue,
    lostCount: lost.length,
    orders: orderCount,
    avgOrderValue,
    itemsSold,
    taxCollected,
    shippingCollected,
    paymentBreakdown,
    topProducts,
  };
};

// Revenue + order count per bucket, with empty days/months filled in
const buildSeries = (liveOrders, startDay, endDay, monthly) => {
  const buckets = [];
  const index = {};
  if (monthly) {
    let d = new Date(startDay.getFullYear(), startDay.getMonth(), 1);
    while (d <= endDay) {
      index[monthKey(d)] = buckets.length;
      buckets.push({ date: d, label: fmtMonth(d), revenue: 0, orders: 0 });
      d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
  } else {
    for (let d = startDay; d <= endDay; d = addDays(d, 1)) {
      index[toISODate(d)] = buckets.length;
      buckets.push({ date: d, label: fmtDay(d), revenue: 0, orders: 0 });
    }
  }
  liveOrders.forEach((o) => {
    const created = new Date(o.createdAt);
    const i = index[monthly ? monthKey(created) : toISODate(created)];
    if (i === undefined) return;
    buckets[i].revenue += o.totalPrice || 0;
    buckets[i].orders += 1;
  });
  return buckets;
};

const pctChange = (cur, prev) => {
  if (!prev) return null;
  return ((cur - prev) / prev) * 100;
};

const Delta = ({ value, invert = false, label }) => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return <span className="slr-delta slr-delta--flat">No prior data</span>;
  }
  const rounded = Math.abs(value) < 0.05 ? 0 : value;
  const up = rounded > 0;
  const flat = rounded === 0;
  const good = flat ? null : invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`slr-delta ${flat ? 'slr-delta--flat' : good ? 'slr-delta--up' : 'slr-delta--down'}`}
      title={label}
    >
      {!flat && <Icon size={12} />}
      {flat ? '0%' : `${Math.abs(rounded).toFixed(1)}%`}
    </span>
  );
};

const SalesReportPage = () => {
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  // Default to 'all' so store orders are immediately visible
  const [preset, setPreset] = useState('all');
  const [range, setRange] = useState(rangeForPreset('all'));
  const [metric, setMetric] = useState('revenue');

  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const list = await getAllOrders({});
      setAllOrders(Array.isArray(list) ? list : []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error loading orders for sales report:', err);
      setError('Could not load orders. Please refresh or try again.');
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== 'custom') setRange(rangeForPreset(key));
  };

  const handleCustomDate = (field, value) => {
    if (!value) return;
    setPreset('custom');
    setRange((prev) => ({ ...prev, [field]: value }));
  };

  const filtered = useMemo(() => ordersInRange(allOrders, range.from, range.to), [allOrders, range]);
  const stats = useMemo(() => computeStats(filtered), [filtered]);

  // Same-length window immediately before the selected range
  const comparable = preset !== 'all';
  const rangeDays = daysBetween(range.from, range.to);
  const prevRange = useMemo(() => {
    const from = parseDay(range.from);
    return { from: toISODate(addDays(from, -rangeDays)), to: toISODate(addDays(from, -1)) };
  }, [range.from, rangeDays]);
  const prevStats = useMemo(
    () => (comparable ? computeStats(ordersInRange(allOrders, prevRange.from, prevRange.to)) : null),
    [allOrders, prevRange, comparable]
  );

  // Visible span: for "all time" start at the first order rather than 2020
  const series = useMemo(() => {
    let start = parseDay(range.from);
    const end = parseDay(range.to);
    if (preset === 'all' && stats.live.length) {
      const first = Math.min(...stats.live.map((o) => new Date(o.createdAt).getTime()));
      const firstDay = new Date(first);
      start = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate());
    }
    const spanDays = Math.round((end - start) / DAY_MS) + 1;
    const monthly = spanDays > 92;
    const current = buildSeries(stats.live, start, end, monthly);
    const previous =
      comparable && !monthly && prevStats
        ? buildSeries(prevStats.live, parseDay(prevRange.from), parseDay(prevRange.to), false)
        : null;
    return { current, previous, monthly };
  }, [stats.live, prevStats, range, preset, comparable, prevRange]);

  const trendTotal = series.current.reduce((s, b) => s + b[metric], 0);
  const bestBucket = series.current.reduce((best, b) => (b[metric] > (best?.[metric] || 0) ? b : best), null);
  const avgPerBucket = series.current.length ? trendTotal / series.current.length : 0;
  const fmtMetric = (v) => (metric === 'revenue' ? formatPrice(Math.round(v)) : Math.round(v * 10) / 10);

  const rangeLabel =
    preset === 'all'
      ? 'All time'
      : range.from === range.to
        ? fmtDay(parseDay(range.from), true)
        : `${fmtDay(parseDay(range.from))} – ${fmtDay(parseDay(range.to), true)}`;
  const compareLabel = comparable
    ? `vs ${fmtDay(parseDay(prevRange.from))} – ${fmtDay(parseDay(prevRange.to), true)}`
    : '';

  const trendData = {
    labels: series.current.map((b) => b.label),
    datasets: [
      {
        label: series.monthly ? 'This period' : 'Selected period',
        data: series.current.map((b) => b[metric]),
        borderColor: '#e38aa2',
        borderWidth: 2,
        backgroundColor: (ctx) => {
          const { chart } = ctx;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return 'rgba(176, 122, 82, 0.12)';
          const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          g.addColorStop(0, 'rgba(176, 122, 82, 0.22)');
          g.addColorStop(1, 'rgba(176, 122, 82, 0)');
          return g;
        },
        pointRadius: series.current.length > 40 ? 0 : 2.5,
        pointHoverRadius: 5,
        pointBackgroundColor: '#e38aa2',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 1.5,
        tension: 0.35,
        fill: true,
      },
      ...(series.previous
        ? [
            {
              label: 'Previous period',
              data: series.previous.map((b) => b[metric]),
              borderColor: '#cdbfb4',
              borderWidth: 1.5,
              borderDash: [4, 4],
              pointRadius: 0,
              pointHoverRadius: 3,
              tension: 0.35,
              fill: false,
            },
          ]
        : []),
    ],
  };

  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        callbacks: {
          title: (items) => items[0]?.label,
          label: (c) => {
            const prev = c.datasetIndex === 1 && series.previous?.[c.dataIndex];
            const prefix = prev ? `${prev.label}: ` : '';
            return ` ${prefix}${metric === 'revenue' ? formatPrice(c.raw || 0) : `${c.raw || 0} orders`}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          maxTicksLimit: 5,
          precision: 0,
          callback: (v) => (metric === 'revenue' ? formatCompact(v) : v),
        },
        grid: { color: GRID_COLOR },
        border: { display: false },
      },
      x: {
        grid: { display: false },
        border: { color: AXIS_COLOR },
        ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10 },
      },
    },
  };

  const paymentTotal = stats.paymentBreakdown.reduce((s, p) => s + p.revenue, 0);
  const paymentData = {
    labels: stats.paymentBreakdown.map((p) => PAYMENT_LABELS[p.method] || p.method),
    datasets: [
      {
        data: stats.paymentBreakdown.map((p) => p.revenue),
        backgroundColor: PALETTE,
        borderWidth: 3,
        borderColor: '#ffffff',
        hoverOffset: 4,
      },
    ],
  };

  const collectedPct = stats.revenue > 0 ? Math.min(100, (stats.paidRevenue / stats.revenue) * 100) : 0;
  const topRevenueMax = stats.topProducts[0]?.revenue || 0;
  const topShareBase = stats.topProducts.reduce((s, p) => s + p.revenue, 0);

  const kpis = [
    {
      key: 'orders',
      label: 'Orders',
      value: stats.orders.toLocaleString('en-IN'),
      icon: ShoppingBag,
      delta: prevStats && pctChange(stats.orders, prevStats.orders),
      note: 'Excludes cancelled & returned',
    },
    {
      key: 'aov',
      label: 'Avg. order value',
      value: formatPrice(stats.avgOrderValue),
      icon: TrendingUp,
      delta: prevStats && pctChange(stats.avgOrderValue, prevStats.avgOrderValue),
      note: 'Net revenue ÷ orders',
    },
    {
      key: 'items',
      label: 'Items sold',
      value: stats.itemsSold.toLocaleString('en-IN'),
      icon: Package,
      delta: prevStats && pctChange(stats.itemsSold, prevStats.itemsSold),
      note: stats.orders ? `${(stats.itemsSold / stats.orders).toFixed(1)} items per order` : 'Units across all orders',
    },
    {
      key: 'lost',
      label: 'Lost to cancellations',
      value: formatPrice(stats.lostRevenue),
      icon: AlertTriangle,
      to: '/admin/orders?status=Cancelled',
      tone: stats.lostRevenue > 0 ? 'danger' : null,
      invert: true,
      delta: prevStats && pctChange(stats.lostRevenue, prevStats.lostRevenue),
      note: `${stats.lostCount} cancelled, rejected or returned`,
    },
  ];

  // CSV Export with detailed order records
  const handleExportCsv = () => {
    if (!filtered.length) return;
    const headers = [
      'Order ID',
      'Date',
      'Customer',
      'Items Count',
      'Payment Method',
      'Payment Status',
      'Order Status',
      'Total Amount (INR)',
    ];
    const rows = filtered.map((o) => [
      o.orderId || o._id,
      toISODate(new Date(o.createdAt)),
      `"${(o.user?.name || o.shippingAddress?.fullName || 'Customer').replace(/"/g, '""')}"`,
      o.orderItems?.reduce((sum, it) => sum + (it.qty || 0), 0) || 0,
      o.paymentMethod || 'COD',
      o.isPaid ? 'Paid' : 'Unpaid',
      o.status,
      o.totalPrice || 0,
    ]);
    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_report_${range.from}_to_${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    try {
      await downloadOrdersPdf();
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

  const isEmpty = !loading && filtered.length === 0;

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Sales Report" />

        <div className="slr-page">
          {/* Heading */}
          <div className="slr-heading">
            <div>
              <span className="slr-eyebrow">Reports</span>
              <h1 className="slr-title">Sales</h1>
              <p className="slr-subtitle">
                {rangeLabel}
                {preset !== 'all' && (
                  <span className="slr-subtitle-meta">
                    {' '}
                    · {rangeDays} {rangeDays === 1 ? 'day' : 'days'}
                  </span>
                )}
              </p>
            </div>
            <div className="slr-heading-actions">
              <button
                type="button"
                className="slr-btn slr-btn-secondary"
                onClick={handleExportPdf}
                title="Download a PDF of all orders (not limited to this period)"
              >
                <FileText size={15} />
                <span>All orders PDF</span>
              </button>
              <button
                type="button"
                className="slr-btn slr-btn-primary"
                onClick={handleExportCsv}
                disabled={loading || !filtered.length}
                title={filtered.length ? `Download ${filtered.length} orders as CSV` : 'No records to export'}
              >
                <FileSpreadsheet size={15} />
                <span>Export CSV</span>
              </button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="slr-toolbar">
            <div className="slr-segmented" role="group" aria-label="Date range">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`slr-seg-btn ${preset === p.key ? 'active' : ''}`}
                  onClick={() => applyPreset(p.key)}
                  aria-pressed={preset === p.key}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className={`slr-dates ${preset === 'custom' ? 'is-custom' : ''}`}>
              <Calendar size={14} className="slr-dates-icon" />
              <input
                type="date"
                value={range.from}
                max={range.to}
                aria-label="From date"
                onChange={(e) => handleCustomDate('from', e.target.value)}
              />
              <span className="slr-dates-sep">–</span>
              <input
                type="date"
                value={range.to}
                min={range.from}
                aria-label="To date"
                onChange={(e) => handleCustomDate('to', e.target.value)}
              />
            </div>

            <div className="slr-toolbar-end">
              {lastUpdated && (
                <span className="slr-updated">
                  Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
              <button
                type="button"
                className="slr-icon-btn"
                onClick={loadOrders}
                disabled={loading}
                title="Reload orders"
                aria-label="Reload orders"
              >
                <RefreshCw size={15} className={loading ? 'slr-spin' : ''} />
              </button>
            </div>
          </div>

          {error && (
            <div className="slr-error" role="alert">
              <AlertTriangle size={16} />
              <span>{error}</span>
              <button type="button" className="slr-link-btn" onClick={loadOrders}>
                Try again
              </button>
            </div>
          )}

          {/* Headline: net revenue + collection */}
          <section className="slr-hero">
            <div className="slr-hero-main">
              <span className="slr-hero-label">Net revenue</span>
              {loading ? (
                <span className="slr-skel slr-skel--hero" />
              ) : (
                <div className="slr-hero-value">{formatPrice(stats.revenue)}</div>
              )}
              <div className="slr-hero-meta">
                {comparable && !loading && (
                  <>
                    <Delta value={pctChange(stats.revenue, prevStats?.revenue)} label={compareLabel} />
                    <span className="slr-hero-compare">
                      {compareLabel} ({formatPrice(prevStats?.revenue || 0)})
                    </span>
                  </>
                )}
                {!comparable && !loading && (
                  <span className="slr-hero-compare">
                    Across {stats.orders.toLocaleString('en-IN')} orders since the store opened
                  </span>
                )}
              </div>
            </div>

            <div className="slr-hero-side">
              <div className="slr-collect-head">
                <span>Collection</span>
                <strong>{loading ? '—' : `${Math.round(collectedPct)}% collected`}</strong>
              </div>
              <div
                className="slr-collect-bar"
                role="img"
                aria-label={`${Math.round(collectedPct)}% of net revenue collected`}
              >
                <span className="slr-collect-fill" style={{ width: `${loading ? 0 : collectedPct}%` }} />
              </div>
              <div className="slr-collect-legend">
                <Link to="/admin/orders" className="slr-collect-item">
                  <span className="slr-dot slr-dot--paid" />
                  <span className="slr-collect-name">Collected</span>
                  <span className="slr-collect-val">{loading ? '—' : formatPrice(stats.paidRevenue)}</span>
                </Link>
                <Link to="/admin/orders?status=pending" className="slr-collect-item">
                  <span className="slr-dot slr-dot--due" />
                  <span className="slr-collect-name">Outstanding</span>
                  <span className="slr-collect-val">{loading ? '—' : formatPrice(stats.outstandingRevenue)}</span>
                </Link>
              </div>
            </div>
          </section>

          {/* KPIs */}
          <div className="slr-grid">
            {kpis.map(({ key, label, value, icon: Icon, to, tone, note, delta, invert }) => {
              const body = (
                <>
                  <div className="slr-kpi-top">
                    <span className="slr-kpi-label">{label}</span>
                    <span className="slr-kpi-icon">
                      <Icon size={16} />
                    </span>
                  </div>
                  {loading ? (
                    <span className="slr-skel slr-skel--value" />
                  ) : (
                    <div className="slr-kpi-value-row">
                      <p className="slr-kpi-value">{value}</p>
                      {comparable && <Delta value={delta} invert={invert} label={compareLabel} />}
                    </div>
                  )}
                  <span className="slr-kpi-note">{note}</span>
                </>
              );
              const cls = `slr-kpi ${tone && !loading ? `slr-kpi--${tone}` : ''} ${to ? 'slr-kpi--link' : ''}`;
              return to ? (
                <Link key={key} to={to} className={cls} aria-label={`${label}: ${loading ? 'Loading' : value}`}>
                  {body}
                </Link>
              ) : (
                <div key={key} className={cls}>
                  {body}
                </div>
              );
            })}
          </div>

          {/* Trend */}
          <section className="slr-panel">
            <header className="slr-panel-head">
              <div>
                <h3 className="slr-panel-title">{metric === 'revenue' ? 'Revenue' : 'Orders'} over time</h3>
                <p className="slr-panel-sub">
                  {series.monthly ? 'Monthly' : 'Daily'} totals
                  {series.previous ? ', compared with the previous period' : ''}
                </p>
              </div>
              <div className="slr-segmented slr-segmented--sm" role="group" aria-label="Chart metric">
                {[
                  { key: 'revenue', label: 'Revenue' },
                  { key: 'orders', label: 'Orders' },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    className={`slr-seg-btn ${metric === m.key ? 'active' : ''}`}
                    onClick={() => setMetric(m.key)}
                    aria-pressed={metric === m.key}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </header>

            {loading ? (
              <div className="slr-skel slr-skel--chart" />
            ) : isEmpty ? (
              <div className="slr-empty">
                <div className="slr-empty-icon">
                  <CalendarX size={20} />
                </div>
                <p className="slr-empty-title">No sales in this period</p>
                <p>Try a wider date range to see how the store is trending.</p>
                {preset !== 'all' && (
                  <button
                    type="button"
                    className="slr-btn slr-btn-secondary slr-btn-sm"
                    onClick={() => applyPreset('all')}
                  >
                    View all-time sales
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="slr-trend-stats">
                  <div>
                    <span>Total</span>
                    <strong>{fmtMetric(trendTotal)}</strong>
                  </div>
                  <div>
                    <span>{series.monthly ? 'Monthly' : 'Daily'} average</span>
                    <strong>{fmtMetric(avgPerBucket)}</strong>
                  </div>
                  <div>
                    <span>Best {series.monthly ? 'month' : 'day'}</span>
                    <strong>
                      {bestBucket ? (
                        <>
                          {fmtMetric(bestBucket[metric])}
                          <em>{bestBucket.label}</em>
                        </>
                      ) : (
                        '—'
                      )}
                    </strong>
                  </div>
                  {series.previous && (
                    <div className="slr-trend-legend">
                      <span className="slr-legend-line" /> This period
                      <span className="slr-legend-line slr-legend-line--prev" /> Previous
                    </div>
                  )}
                </div>
                <div className="slr-chart">
                  <Line data={trendData} options={trendOptions} />
                </div>
              </>
            )}
          </section>

          {/* Payments + statement */}
          <div className="slr-row">
            <section className="slr-panel">
              <header className="slr-panel-head">
                <div>
                  <h3 className="slr-panel-title">Payment methods</h3>
                  <p className="slr-panel-sub">Share of gross revenue by channel</p>
                </div>
              </header>
              {loading ? (
                <div className="slr-skel slr-skel--chart slr-skel--short" />
              ) : stats.paymentBreakdown.length === 0 ? (
                <div className="slr-empty slr-empty--compact">
                  <div className="slr-empty-icon">
                    <CreditCard size={20} />
                  </div>
                  <p>No payments in this period.</p>
                </div>
              ) : (
                <div className="slr-pay">
                  <div className="slr-pay-chart">
                    <Doughnut
                      data={paymentData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '74%',
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            callbacks: { label: (c) => ` ${c.label}: ${formatPrice(c.raw || 0)}` },
                          },
                        },
                      }}
                    />
                    <div className="slr-pay-center">
                      <span>{formatCompact(paymentTotal)}</span>
                      <small>Gross</small>
                    </div>
                  </div>
                  <ul className="slr-pay-list">
                    {stats.paymentBreakdown.map((p, i) => {
                      const share = paymentTotal ? (p.revenue / paymentTotal) * 100 : 0;
                      return (
                        <li key={p.method}>
                          <span className="slr-pay-swatch" style={{ background: PALETTE[i % PALETTE.length] }} />
                          <div className="slr-pay-text">
                            <span className="slr-pay-name">{PAYMENT_LABELS[p.method] || p.method}</span>
                            <span className="slr-pay-sub">
                              {p.orders} {p.orders === 1 ? 'order' : 'orders'}
                            </span>
                          </div>
                          <div className="slr-pay-figs">
                            <span className="slr-pay-amt">{formatPrice(p.revenue)}</span>
                            <span className="slr-pay-share">{share.toFixed(0)}%</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </section>

            <section className="slr-panel">
              <header className="slr-panel-head">
                <div>
                  <h3 className="slr-panel-title">Revenue statement</h3>
                  <p className="slr-panel-sub">How gross sales become net revenue</p>
                </div>
              </header>
              <dl className="slr-statement">
                <div className="slr-st-row">
                  <dt>Gross sales</dt>
                  <dd>{loading ? '—' : formatPrice(stats.grossRevenue)}</dd>
                </div>
                <div className="slr-st-row slr-st-row--minus">
                  <dt>Refunds</dt>
                  <dd>{loading ? '—' : `− ${formatPrice(stats.refunds)}`}</dd>
                </div>
                <div className="slr-st-row slr-st-row--total">
                  <dt>Net revenue</dt>
                  <dd>{loading ? '—' : formatPrice(stats.revenue)}</dd>
                </div>
                <div className="slr-st-group">Included in gross sales</div>
                <div className="slr-st-row slr-st-row--sub">
                  <dt>Tax collected</dt>
                  <dd>{loading ? '—' : formatPrice(stats.taxCollected)}</dd>
                </div>
                <div className="slr-st-row slr-st-row--sub">
                  <dt>Shipping charged</dt>
                  <dd>{loading ? '—' : formatPrice(stats.shippingCollected)}</dd>
                </div>
                <div className="slr-st-group">Not counted</div>
                <div className="slr-st-row slr-st-row--sub slr-st-row--lost">
                  <dt>Cancelled, rejected &amp; returned</dt>
                  <dd>{loading ? '—' : formatPrice(stats.lostRevenue)}</dd>
                </div>
              </dl>
            </section>
          </div>

          {/* Top products */}
          <section className="slr-panel">
            <header className="slr-panel-head">
              <div>
                <h3 className="slr-panel-title">Top products</h3>
                <p className="slr-panel-sub">Ranked by revenue in the selected period</p>
              </div>
              <Link to="/admin/report/products" className="slr-link">
                Product report <ArrowUpRight size={13} />
              </Link>
            </header>
            {loading ? (
              <div className="slr-skel slr-skel--chart slr-skel--short" />
            ) : stats.topProducts.length === 0 ? (
              <div className="slr-empty slr-empty--compact">
                <div className="slr-empty-icon">
                  <Package size={20} />
                </div>
                <p>No product sales in this period.</p>
              </div>
            ) : (
              <div className="slr-table-wrap">
                <table className="slr-table">
                  <thead>
                    <tr>
                      <th className="slr-c-rank">#</th>
                      <th>Product</th>
                      <th className="slr-num">Units</th>
                      <th className="slr-num">Revenue</th>
                      <th className="slr-c-share">Share of top 10</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topProducts.map((p, i) => {
                      const share = topShareBase ? (p.revenue / topShareBase) * 100 : 0;
                      const bar = topRevenueMax ? (p.revenue / topRevenueMax) * 100 : 0;
                      return (
                        <tr key={p.name}>
                          <td className="slr-c-rank">
                            <span className={`slr-rank ${i < 3 ? 'slr-rank--top' : ''}`}>{i + 1}</span>
                          </td>
                          <td>
                            <div className="slr-prod">
                              {p.image ? (
                                <img
                                  src={mediaUrl(p.image)}
                                  alt=""
                                  className="slr-thumb"
                                  onError={(e) => {
                                    e.currentTarget.style.visibility = 'hidden';
                                  }}
                                />
                              ) : (
                                <span className="slr-thumb slr-thumb--empty">
                                  <Package size={14} />
                                </span>
                              )}
                              <span className="slr-prod-name" title={p.name}>
                                {p.name}
                              </span>
                            </div>
                          </td>
                          <td className="slr-num">{p.totalSold}</td>
                          <td className="slr-num slr-strong">{formatPrice(p.revenue)}</td>
                          <td className="slr-c-share">
                            <div className="slr-share">
                              <div className="slr-share-track">
                                <div className="slr-share-bar" style={{ width: `${bar}%` }} />
                              </div>
                              <span className="slr-share-val">{share.toFixed(0)}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default SalesReportPage;
